import React, { useState } from 'react';
import { Plus, Trash2, Code, Table2 } from 'lucide-react';
import TransformsEditor from './TransformsEditor';
import { TARGET_FIELDS } from './constants';

// Visual editor for the field mapping. Each row maps one of our employee
// fields (target) to a field in the source API response, with an optional
// chain of transforms. A JSON toggle stays available for power users / quick
// paste.
//
// value shape: { [targetField]: { from: string, transforms?: [...] } }

const MappingEditor = ({ value, onChange }) => {
  const [mode, setMode] = useState('visual');
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonError, setJsonError] = useState(null);

  const rows = Object.entries(value ?? {}).map(([target, def]) => ({
    target,
    from: def?.from ?? '',
    transforms: def?.transforms ?? [],
  }));

  const usedTargets = new Set(rows.map((r) => r.target));
  const available = TARGET_FIELDS.filter((f) => !usedTargets.has(f));

  const commitRows = (nextRows) => {
    const obj = {};
    for (const r of nextRows) {
      if (!r.target) continue;
      obj[r.target] = { from: r.from || '', ...(r.transforms?.length ? { transforms: r.transforms } : {}) };
    }
    onChange(obj);
  };

  const addRow = () => {
    const target = available[0] ?? '';
    commitRows([...rows, { target, from: '', transforms: [] }]);
  };
  const removeRow = (i) => commitRows(rows.filter((_, idx) => idx !== i));
  const patchRow = (i, patch) =>
    commitRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const enterJsonMode = () => {
    setJsonDraft(JSON.stringify(value ?? {}, null, 2));
    setJsonError(null);
    setMode('json');
  };
  const applyJson = (text) => {
    setJsonDraft(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed);
    } catch (e) {
      setJsonError(e.message);
    }
  };

  return (
    <div className="tw-space-y-3">
      <div className="tw-flex tw-justify-end">
        <div className="tw-inline-flex tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
          <ModeBtn active={mode === 'visual'} onClick={() => setMode('visual')} icon={<Table2 className="tw-w-3.5 tw-h-3.5" />}>
            Visual
          </ModeBtn>
          <ModeBtn active={mode === 'json'} onClick={enterJsonMode} icon={<Code className="tw-w-3.5 tw-h-3.5" />}>
            JSON
          </ModeBtn>
        </div>
      </div>

      {mode === 'visual' ? (
        <div className="tw-space-y-2">
          {rows.length === 0 && (
            <div className="tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-text-sm tw-text-gray-500 tw-text-center">
              No fields mapped yet. Click <strong>Add field</strong>.
            </div>
          )}
          {rows.map((r, i) => (
            <div key={i} className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3 tw-bg-white">
              <div className="tw-grid tw-grid-cols-[180px_1fr_40px] tw-gap-2 tw-items-center">
                <select
                  value={r.target}
                  onChange={(e) => patchRow(i, { target: e.target.value })}
                  className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white tw-font-medium"
                >
                  <option value={r.target}>{r.target}</option>
                  {available.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={r.from}
                  onChange={(e) => patchRow(i, { from: e.target.value })}
                  placeholder="source field (e.g. firstName, or $ for whole record)"
                  className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg focus:tw-border-indigo-500 focus:tw-outline-none"
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="tw-p-2 tw-text-gray-400 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                  aria-label="Remove field"
                >
                  <Trash2 className="tw-w-4 tw-h-4" />
                </button>
              </div>
              <div className="tw-mt-2 tw-pl-1">
                <TransformsEditor
                  transforms={r.transforms}
                  onChange={(transforms) => patchRow(i, { transforms })}
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            disabled={available.length === 0}
            className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-indigo-600 tw-bg-indigo-50 tw-border-0 tw-rounded-lg tw-cursor-pointer hover:tw-bg-indigo-100 disabled:tw-opacity-50"
          >
            <Plus className="tw-w-4 tw-h-4" /> Add field
          </button>
        </div>
      ) : (
        <div>
          <textarea
            value={jsonDraft}
            onChange={(e) => applyJson(e.target.value)}
            rows={12}
            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-font-mono tw-border tw-border-gray-200 tw-rounded-lg focus:tw-border-indigo-500 focus:tw-outline-none"
          />
          {jsonError && <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">Invalid JSON: {jsonError}</p>}
        </div>
      )}
    </div>
  );
};

const ModeBtn = ({ active, onClick, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-border-0 tw-cursor-pointer',
      active ? 'tw-bg-indigo-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50',
    ].join(' ')}
  >
    {icon}
    {children}
  </button>
);

export default MappingEditor;
